import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Search, CalendarClock, ChevronLeft, ChevronRight, X, RefreshCw, Mail, CalendarPlus, KeyRound, Square } from 'lucide-react';
import { useSocket } from '../Context/SocketContext';
import { infoUser } from '../utils/auth';
import Loader from './Loader';
import MailBuilder from './MailBuilder';

const PER_PAGE = 50
const idOf = (r) => r.idUser ?? r.id_db ?? r.id ?? r.user_id

const Bool = ({ v, on = 'Oui', off = 'Non' }) => (
    <span className={`ub-flag ${v ? 'ok' : 'ko'}`}>{v ? on : off}</span>
)

const UserBackupList = () => {
    const { socket } = useSocket()
    const userToken = infoUser()

    const [list, setList] = useState(null)
    const [search, setSearch] = useState('')
    const [fPassword, setFPassword] = useState('')
    const [fForgot, setFForgot] = useState('')
    const [fNew, setFNew] = useState('')
    const [fPremium, setFPremium] = useState('')     // '' | 'active' | 'expired' | 'nonpremium'
    const [expiredSince, setExpiredSince] = useState('')
    const [showAll, setShowAll] = useState(false)
    const [page, setPage] = useState(0)

    const [selected, setSelected] = useState({})
    const [addDays, setAddDays] = useState(7)
    const [bulkLoading, setBulkLoading] = useState(false)

    const [recoveryRunning, setRecoveryRunning] = useState(false)
    const [recoveryDelay, setRecoveryDelay] = useState(1)
    const [recoveryProgress, setRecoveryProgress] = useState({ done: 0, total: 0 })
    const recoveryCancel = useRef(false)

    const [edit, setEdit] = useState(null)
    const [editDate, setEditDate] = useState('')
    const [savingEdit, setSavingEdit] = useState(false)

    const [mailOpen, setMailOpen] = useState(false)
    const scrollRef = useRef(null)

    // la molette verticale sur le tableau scrolle la page (et pas le conteneur horizontal)
    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        const handler = (e) => {
            if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return   // scroll horizontal → natif
            let p = el.parentElement
            while (p && p !== document.body) {
                const oy = getComputedStyle(p).overflowY
                if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) {
                    p.scrollTop += e.deltaY
                    e.preventDefault()
                    return
                }
                p = p.parentElement
            }
        }
        el.addEventListener('wheel', handler, { passive: false })
        return () => el.removeEventListener('wheel', handler)
    }, [list])

    const fetchList = () => {
        setList(null); setSelected({})
        try{
            if(socket){
                socket.emit('empire_get_user_backup', { token: userToken.expecttoken }, (res) => {
                    setList(Array.isArray(res) ? res : [])
                })

            }

        }catch(err){

        }
    }
    useEffect(() => { if (socket) fetchList() }, [socket])

    const endOf = (r) => (r.dateend && moment(r.dateend).isValid()) ? moment(r.dateend) : null
    const isPremium = (r) => { const e = endOf(r); return e && e.isAfter(moment()) }

    const filtered = useMemo(() => {
        if (!list) return []
        const s = search.trim().toLowerCase()
        const since = parseInt(expiredSince) || 0
        return list.filter(r => {
            if (s && !(`${r.email || ''} ${r.name || ''}`.toLowerCase().includes(s))) return false
            if (fPassword === 'yes' && !r.password) return false
            if (fPassword === 'no' && r.password) return false
            if (fForgot === 'yes' && !r.passwordforgot) return false
            if (fForgot === 'no' && r.passwordforgot) return false
            if (fNew === 'yes' && !r.newuser) return false
            if (fNew === 'no' && r.newuser) return false
            if (fPremium === 'active' && !isPremium(r)) return false
            if (fPremium === 'expired' && !(endOf(r) && !isPremium(r))) return false
            if (fPremium === 'nonpremium' && isPremium(r)) return false
            if (since > 0) {
                const e = endOf(r)
                if (!e || e.isAfter(moment())) return false           // pas expiré
                if (e.isBefore(moment().subtract(since, 'days'))) return false // expiré depuis trop longtemps
            }
            return true
        })
    }, [list, search, fPassword, fForgot, fNew, fPremium, expiredSince])

    useEffect(() => { setPage(0) }, [search, fPassword, fForgot, fNew, fPremium, expiredSince])

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE))
    const pageRows = showAll ? filtered : filtered.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

    /* ---------- sélection ---------- */
    const toggleSel = (r) => setSelected(s => ({ ...s, [idOf(r)]: !s[idOf(r)] }))
    const allSelected = filtered.length > 0 && filtered.every(r => selected[idOf(r)])
    const toggleAll = () => setSelected(allSelected ? {} : filtered.reduce((a, r) => { a[idOf(r)] = true; return a }, {}))
    const selectedRows = filtered.filter(r => selected[idOf(r)])

    const renderPremium = (r) => {
        const end = endOf(r)
        if (!end) return <span className='ub-endbox none'>—</span>
        if (end.isAfter(moment())) {
            return (
                <span className='ub-endbox ok'>
                    <span className='d'>{end.diff(moment(), 'days')} j</span>
                    <span className='dt'>{end.format('DD/MM/YY')}</span>
                </span>
            )
        }
        const daysExpired = moment().diff(end, 'days')
        return (
            <span className='ub-endbox ko'>
                <span className='d white'>-{daysExpired} j</span>
                <span className='dt'>expiré · {end.format('DD/MM/YY')}</span>
            </span>
        )
    }

    /* ---------- bulk: ajouter des jours ---------- */
    const applyAddDays = async (days) => {
        const n = parseInt(days) || 0
        if (!n || selectedRows.length === 0) return
        setBulkLoading(true)
        for (const r of selectedRows) {
            const base = isPremium(r) ? endOf(r) : moment()   // premium → depuis dateend ; expiré → aujourd'hui
            const newDate = base.clone().add(n, 'days').format('YYYY-MM-DD HH:mm:ss')
            // eslint-disable-next-line no-await-in-loop
            const ok = await new Promise(res => socket.emit('empire_edit_premium_user', { token: userToken.expecttoken, idUser: idOf(r), date: newDate }, o => res(!!o)))
            if (ok) setList(l => l.map(x => idOf(x) === idOf(r) ? { ...x, dateend: newDate } : x))
        }
        setBulkLoading(false)
    }

    /* ---------- bulk: recovery (password forgot) ---------- */
    const runRecovery = async () => {
        if (recoveryRunning || selectedRows.length === 0) return
        recoveryCancel.current = false
        setRecoveryRunning(true)
        const rows = selectedRows
        setRecoveryProgress({ done: 0, total: rows.length })
        const d = Math.max(0, parseFloat(recoveryDelay) || 0)
        for (let i = 0; i < rows.length; i++) {
            if (recoveryCancel.current) break
            const r = rows[i]
            // eslint-disable-next-line no-await-in-loop
            await new Promise(res => socket.emit('empire_add_password_forgot', { token: userToken.expecttoken, email: r.email }, (ok) => {
                if (ok) setList(l => l.map(x => idOf(x) === idOf(r) ? { ...x, passwordforgot: true } : x))
                res()
            }))
            setRecoveryProgress(p => ({ ...p, done: p.done + 1 }))
            if (i < rows.length - 1 && d > 0 && !recoveryCancel.current) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise(r2 => setTimeout(r2, d * 1000))
            }
        }
        setRecoveryRunning(false)
    }
    const stopRecovery = () => { recoveryCancel.current = true }

    /* ---------- édition unitaire ---------- */
    const openEdit = (r) => { setEdit(r); setEditDate(endOf(r) ? endOf(r).format('YYYY-MM-DD') : '') }
    const saveEdit = () => {
        if (!edit) return
        setSavingEdit(true)
        socket.emit('empire_edit_premium_user', { token: userToken.expecttoken, idUser: idOf(edit), date: editDate || null }, (ok) => {
            setSavingEdit(false)
            if (ok) { setList(l => l.map(r => (idOf(r) === idOf(edit) ? { ...r, dateend: editDate || null } : r))); setEdit(null) }
        })
    }

    return (
        <div className='ub-list'>
            <div className='df aic jcsb fw gap1 mb2'>
                <div className='ub-search df aic gap1'>
                    <Search size={17} color='#8f909c' />
                    <input placeholder='Rechercher email / nom...' value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
                </div>
                <button onClick={fetchList} className='btn cw df aic gap1'><RefreshCw size={15} color='#fff' /> Actualiser</button>
            </div>

            <div className='df gap1 fw mb2 ub-filters'>
                <select value={fPassword} onChange={(e) => setFPassword(e.currentTarget.value)}><option value=''>Backup : tous</option><option value='yes'>Fait</option><option value='no'>Non fait</option></select>
                <select value={fForgot} onChange={(e) => setFForgot(e.currentTarget.value)}><option value=''>Recovery : tous</option><option value='yes'>Demandé</option><option value='no'>Non</option></select>
                <select value={fNew} onChange={(e) => setFNew(e.currentTarget.value)}><option value=''>Nouveau : tous</option><option value='yes'>Oui</option><option value='no'>Non</option></select>
                <select value={fPremium} onChange={(e) => setFPremium(e.currentTarget.value)}><option value=''>Premium : tous</option><option value='active'>Actif</option><option value='expired'>Expiré</option><option value='nonpremium'>Non premium</option></select>
                <input type='number' min='1' placeholder='Expiré depuis (j)' value={expiredSince} onChange={(e) => setExpiredSince(e.currentTarget.value)} className='ub-num' />
                <label className='df aic gap1 cw fr fs12' style={{ cursor: 'pointer' }}>
                    <input type='checkbox' checked={showAll} onChange={() => setShowAll(v => !v)} /> Tout afficher
                </label>
            </div>

            {list === null ? (
                <div className='py2 df jcc'><Loader /></div>
            ) : (
                <>
                    {/* Barre d'actions sélection */}
                    {selectedRows.length > 0 && (
                        <div className='ub-bulk df aic jcsb fw gap1 mb2'>
                            <span className='cw fr fs12'>{selectedRows.length} sélectionné(s)</span>
                            <div className='df aic gap1 fw'>
                                <span className='cw fr fs12' style={{ opacity: 0.7 }}>Ajouter :</span>
                                <button disabled={bulkLoading} onClick={() => applyAddDays(7)} className='btn cw'>+1 sem.</button>
                                <button disabled={bulkLoading} onClick={() => applyAddDays(14)} className='btn cw'>+2 sem.</button>
                                <input type='number' min='1' value={addDays} onChange={(e) => setAddDays(e.currentTarget.value)} className='ub-num' style={{ width: '70px' }} />
                                <button disabled={bulkLoading} onClick={() => applyAddDays(addDays)} className='btn cs df aic gap1'>
                                    {bulkLoading ? <span className='loader sm'></span> : <><CalendarPlus size={15} color='#fff' /> jours</>}
                                </button>
                                <button onClick={() => setMailOpen(true)} className='btn cs df aic gap1'><Mail size={15} color='#fff' /> Email</button>

                                <span className='cw fr fs12' style={{ opacity: 0.7, marginLeft: '8px' }}>Recovery · délai (s)</span>
                                <input type='number' min='0' step='0.5' value={recoveryDelay} onChange={(e) => setRecoveryDelay(e.currentTarget.value)} className='ub-num' style={{ width: '60px' }} />
                                {recoveryRunning ? (
                                    <button onClick={stopRecovery} className='btn cw df aic gap1'><Square size={13} color='#f71717' /> Stop ({recoveryProgress.done}/{recoveryProgress.total})</button>
                                ) : (
                                    <button onClick={runRecovery} className='btn cw df aic gap1'><KeyRound size={14} color='#fff' /> Recovery</button>
                                )}
                            </div>
                        </div>
                    )}

                    {recoveryRunning && (
                        <div className='mb-progress mb2'>
                            <div className='mb-bar'><div className='mb-bar-fill' style={{ width: `${recoveryProgress.total ? Math.round((recoveryProgress.done / recoveryProgress.total) * 100) : 0}%` }}></div></div>
                            <p className='cw fr fs12 mt1'>Recovery {recoveryProgress.done}/{recoveryProgress.total}</p>
                        </div>
                    )}

                    <p className='cw fr fs12 mb1' style={{ opacity: 0.7 }}>{filtered.length} résultat(s){showAll ? '' : ` · page ${page + 1}/${totalPages}`}</p>

                    <div className='ub-scroll' ref={scrollRef}>
                        <div className='ubl-table'>
                            <div className='ubl-head'>
                                <span className='tac'><input type='checkbox' checked={allSelected} onChange={toggleAll} /></span>
                                <span>Email</span><span>Nom</span>
                                <span className='tac'>Backup</span><span className='tac'>Recovery</span><span className='tac'>Nouveau</span>
                                <span>Date pay</span><span className='tac'>Fin premium</span><span className='tar'>Prix</span><span>Version</span>
                                <span className='tac'>Action</span>
                            </div>
                            {pageRows.map((r, i) => (
                                <div className={`ubl-row ${selected[idOf(r)] ? 'sel' : ''}`} key={idOf(r) ?? i}>
                                    <span className='tac'><input type='checkbox' checked={!!selected[idOf(r)]} onChange={() => toggleSel(r)} /></span>
                                    <span className='cw truncate' title={r.email}>{r.email}</span>
                                    <span className='cw truncate' title={r.name}>{r.name}</span>
                                    <span className='tac'><Bool v={r.password} on='Fait' off='Non' /></span>
                                    <span className='tac'><Bool v={r.passwordforgot} on='Oui' off='—' /></span>
                                    <span className='tac'><Bool v={r.newuser} on='Oui' off='—' /></span>
                                    <span className='cw fs12'>{r.datepay ? moment(r.datepay).format('DD/MM/YY') : '—'}</span>
                                    <span className='tac'>{renderPremium(r)}</span>
                                    <span className='tar cw fb'>{r.price != null ? `${r.price}€` : '—'}</span>
                                    <span className='cw truncate fs12' title={r.versionhash}>{r.versionhash || '—'}</span>
                                    <span className='tac'>
                                        <button onClick={() => openEdit(r)} className='pay-act-btn' title='Modifier premium'><CalendarClock size={16} color='#f8cc28' /></button>
                                    </span>
                                </div>
                            ))}
                            {pageRows.length === 0 && <p className='ub-empty'>Aucun résultat</p>}
                        </div>
                    </div>

                    {!showAll && totalPages > 1 && (
                        <div className='df aic jcc gap1 mt2 ub-pager'>
                            <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} className='pay-act-btn'><ChevronLeft size={18} color='#fff' /></button>
                            <span className='cw fr fs12'>Page {page + 1} / {totalPages}</span>
                            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} className='pay-act-btn'><ChevronRight size={18} color='#fff' /></button>
                        </div>
                    )}
                </>
            )}

            {/* Modal édition premium */}
            {edit && (
                <div className='modal-overlay' onClick={() => setEdit(null)}>
                    <div className='modal-box' onClick={(e) => e.stopPropagation()}>
                        <div className='df aic jcsb mb2'>
                            <p className='fb cw fs18'>Modifier premium</p>
                            <button onClick={() => setEdit(null)} className='pay-act-btn'><X size={18} color='#fff' /></button>
                        </div>
                        <p className='fr cw fs12 mb2' style={{ opacity: 0.6 }}>{edit.email}</p>
                        <label className='fr cw fs12'>Fin de premium</label>
                        <input type='date' value={editDate} onChange={(e) => setEditDate(e.currentTarget.value)} className='premium-date-input mt1' />
                        <div className='df gap1 mt2'>
                            <button onClick={() => setEditDate('')} className='btn cw'>Vider</button>
                            <button onClick={saveEdit} disabled={savingEdit} className='btn cs flex1 df aic jcc'>{savingEdit ? <span className='loader sm'></span> : 'Enregistrer'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Builder email */}
            {mailOpen && (
                <MailBuilder emails={selectedRows.map(r => r.email).filter(Boolean)} onClose={() => setMailOpen(false)} />
            )}
        </div>
    );
};

export default UserBackupList;
